/**
 * Simple in-memory cache for API GET responses.
 * Prevents re-fetching master data on every page navigation.
 * Cache auto-expires after TTL (default 30s).
 * Call invalidate() after any write operation (POST/PUT/DELETE).
 */

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const DEFAULT_TTL_MS = 30_000; // 30 seconds

/**
 * Get cached data or fetch from API.
 * @param key - Cache key (e.g., "items_all", "suppliers_all")
 * @param fetcher - Async function that fetches data from API
 * @param ttlMs - Time to live in milliseconds (default 30s)
 */
export async function cachedGet<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS
): Promise<T> {
  const now = Date.now();
  const entry = cache.get(key) as CacheEntry<T> | undefined;

  if (entry && entry.expiresAt > now) {
    return entry.data;
  }

  const data = await fetcher();
  cache.set(key, { data, expiresAt: now + ttlMs });
  return data;
}

/**
 * Invalidate specific cache keys (call after POST/PUT/DELETE).
 * Supports prefix matching — invalidate("items") clears "items_all", "items_bakery", etc.
 */
export function invalidate(...prefixes: string[]): void {
  if (prefixes.length === 0) {
    cache.clear();
    return;
  }
  const keys = Array.from(cache.keys());
  for (const key of keys) {
    if (prefixes.some(p => key.startsWith(p))) {
      cache.delete(key);
    }
  }
}

/**
 * Clear entire cache (e.g., on logout or fiscal year switch).
 */
export function clearCache(): void {
  cache.clear();
}
